import { lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider } from './contexts/AuthContext'
import { ToastProvider } from './components/Toast'
import { CheckinProvider } from './contexts/CheckinContext'
import { Layout } from './components/Layout'
import { useAuth } from './hooks/useAuth'

// Eagerly loaded — public entry points, must be fast
import Landing from './pages/Landing'
import Auth from './pages/Auth'
import Invite from './pages/Invite'
import AboutV1 from './pages/AboutV1'
import AboutV2 from './pages/AboutV2'
import AboutV3 from './pages/AboutV3'
import MissionPage from './pages/MissionPage'

// Lazily loaded — authenticated pages, split into separate chunks
const Dashboard = lazy(() => import('./pages/Dashboard'))
const Circle = lazy(() => import('./pages/Circle'))
const Profile = lazy(() => import('./pages/Profile'))
const CheckInResponse = lazy(() => import('./pages/CheckInResponse'))

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-stone-50 dark:bg-stone-900">
        <div className="flex flex-col items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-amber-500 flex items-center justify-center">
            <span className="text-2xl">🔆</span>
          </div>
          <p className="text-stone-400 text-sm">Loading...</p>
        </div>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/auth" replace />
  }

  return children
}

function PublicOnlyRoute({ children }) {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-stone-50 dark:bg-stone-900">
        <div className="flex flex-col items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-amber-500 flex items-center justify-center">
            <span className="text-2xl">🔆</span>
          </div>
          <p className="text-stone-400 text-sm">Loading...</p>
        </div>
      </div>
    )
  }

  if (user) {
    return <Navigate to="/dashboard" replace />
  }

  return children
}

function AppRoutes() {
  return (
    <Layout>
      <Suspense fallback={
        <div className="min-h-[100dvh] flex items-center justify-center bg-stone-50 dark:bg-stone-900">
          <div className="flex flex-col items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-amber-500 flex items-center justify-center">
              <span className="text-2xl">🔆</span>
            </div>
            <p className="text-stone-400 text-sm">Loading...</p>
          </div>
        </div>
      }>
      <Routes>
        {/* Public routes */}
        <Route
          path="/"
          element={
            <PublicOnlyRoute>
              <Landing />
            </PublicOnlyRoute>
          }
        />
        <Route
          path="/auth"
          element={
            <PublicOnlyRoute>
              <Auth />
            </PublicOnlyRoute>
          }
        />

        {/* Protected routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/circle"
          element={
            <ProtectedRoute>
              <Circle />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/checkin/:requestId"
          element={
            <ProtectedRoute>
              <CheckInResponse />
            </ProtectedRoute>
          }
        />

        {/* Invite route — public, no auth required */}
        <Route path="/invite/:token" element={<Invite />} />

        {/* About route — public, accessible to all */}
        <Route path="/about" element={<AboutV1 />} />
        <Route path="/about-b" element={<AboutV2 />} />
        <Route path="/mission" element={<MissionPage />} />
        <Route path="/about-c" element={<AboutV3 />} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    </Layout>
  )
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ToastProvider>
          <CheckinProvider>
            <AppRoutes />
          </CheckinProvider>
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  )
}

export default App
